import React, { type CSSProperties, type ComponentType, type ElementType, type ReactNode, useMemo } from 'react';
import cn from 'classnames';

import { _isNotBlank, _notNil } from '@/littledash';

import SortableItem from '@/components/Draggable/SortableItem';
import SortableList from '@/components/Draggable/SortableList';
import AddButton from '@/components/UI/AddButton';
import RemoveButton from '@/components/UI/RemoveButton';

interface SortableConfig {
  droppableId: string;
  onReorder: (sourceIndex: number, destinationIndex: number) => void;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

interface HtmlProps {
  className?: string;
  style?: CSSProperties;
}

interface ListItem {
  id: string;
}

interface ListProps<T extends ListItem> {
  data?: Array<T>;
  renderItem: (value: T, index: number, array: Array<T>) => ReactNode;
  removeButtonClasses?: string;
  canAmend?: boolean;
  canAdd?: boolean;
  canRemove?: boolean;
  onAddClick?: () => void;
  onRemoveClick?: (index: number) => void;
  addButtonText?: string;
  addButtonProps?: HtmlProps;
  isRemoveButtonFluid?: boolean;
  itemProps?: HtmlProps;
  itemsContainerProps?: HtmlProps;
  ItemsContainerType?: ElementType;
  containerProps?: HtmlProps;
  sortable?: SortableConfig;
}

const DraggableList = <T extends ListItem>({
  data = [],
  renderItem,
  canAmend,
  canAdd,
  canRemove,
  onAddClick,
  onRemoveClick,
  addButtonText = '',
  addButtonProps = {},
  isRemoveButtonFluid = true,
  itemProps = {},
  itemsContainerProps = {},
  ItemsContainerType = 'span',
  containerProps = {},
  removeButtonClasses = '',
  sortable,
}: ListProps<T>): React.JSX.Element => {
  const items = useMemo(() => data.map(renderItem), [data, renderItem]);
  const { className: itemsContainerClassName, ...restItemsContainerProps } = itemsContainerProps;

  const isSortable = _notNil(sortable);
  const showRemoveInOuterRow = canAmend === true && !isSortable;

  const childrenList = React.Children.map(items, (child, index) => {
    const { className: itemClassName, ...restItemProps } = itemProps;
    return (
      <div
        key={data[index].id}
        className={cn(
          'flex flex-row',
          isSortable ? 'items-center' : cn(isRemoveButtonFluid ? 'items-start justify-between' : 'items-center', 'mb3'),
          !isSortable ? itemClassName : undefined
        )}
        {...(!isSortable ? restItemProps : {})}
      >
        <div className={cn('min-w-0', { 'flex-auto': isRemoveButtonFluid })}>{child}</div>
        {showRemoveInOuterRow ? (
          <RemoveButton
            className={cn('ml2 flex-shrink-0', removeButtonClasses)}
            style={{ visibility: canRemove === true ? 'visible' : 'hidden' }}
            onClick={() => {
              onRemoveClick?.(index);
            }}
          />
        ) : null}
      </div>
    );
  });

  const ItemsContainer = ItemsContainerType ?? 'span';

  const itemsContent = isSortable ? (
    <SortableList
      droppableId={sortable.droppableId}
      onReorder={sortable.onReorder}
      disabled={sortable.disabled}
      className={sortable.className}
      style={sortable.style}
    >
      {childrenList}
    </SortableList>
  ) : (
    childrenList
  );

  return (
    <div {...containerProps} data-test-component="List" data-test-element="container">
      <ItemsContainer className={itemsContainerClassName} {...restItemsContainerProps}>
        {itemsContent}
      </ItemsContainer>
      {canAdd === true && canAmend === true && (
        <AddButton onClick={onAddClick} className={cn('mt3', addButtonProps.className)}>
          {_isNotBlank(addButtonText) ? addButtonText : 'Add item'}
        </AddButton>
      )}
    </div>
  );
};

export default DraggableList;

export const asListItem =
  <P,>(Component: ComponentType<P & { index: number }>, props?: Partial<P>) =>
  (item: ListItem, index: number): ReactNode => {
    const merged = { ...props, ...item, index } as unknown as P & { index: number };
    return <Component {...merged} />;
  };

export interface SortableRowRemoveConfig {
  enabled: boolean;
  visible: boolean;
  onRemoveClick: (itemIndex: number) => void;
  removeButtonClasses?: string;
}

interface SortableItemConfig {
  canSort: boolean;
  itemClassName?: string;
  rowRemove?: SortableRowRemoveConfig;
}

export const asSortableListItem =
  (sortableConfig: SortableItemConfig) =>
  <P,>(Component: ComponentType<P & { index: number }>, props?: Partial<P>) =>
  (item: ListItem, index: number): ReactNode => {
    const merged = { ...props, ...item, index } as unknown as P & { index: number };
    const component = <Component {...merged} />;
    const rowRemove = sortableConfig.rowRemove;

    const removeForSortable =
      rowRemove?.enabled === true
        ? {
            enabled: true as const,
            visible: rowRemove.visible,
            onRemoveClick: () => {
              rowRemove.onRemoveClick(index);
            },
            removeButtonClasses: rowRemove.removeButtonClasses,
          }
        : undefined;

    const rowClassName = cn(
      'flex flex-row justify-between items-center w-100 hide-child',
      sortableConfig.itemClassName
    );

    if (sortableConfig.canSort) {
      return (
        <SortableItem
          key={item.id}
          id={String(item.id)}
          index={index}
          showDraggableIcon
          remove={removeForSortable}
          className={rowClassName}
        >
          <div className="flex flex-row">{component}</div>
        </SortableItem>
      );
    }

    return (
      <div className={rowClassName}>
        <div className="min-w-0 flex-auto">
          <div className="flex flex-row">{component}</div>
        </div>
        {rowRemove?.enabled === true ? (
          <div
            className="ml2 flex flex-shrink-0 items-center"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <RemoveButton
              className={cn('flex-shrink-0', rowRemove.removeButtonClasses)}
              style={{ visibility: rowRemove.visible ? 'visible' : 'hidden' }}
              onClick={() => {
                rowRemove.onRemoveClick(index);
              }}
            />
          </div>
        ) : null}
      </div>
    );
  };
